/**
 * Messages API - Direct messaging for NLT Radio
 * Uses same endpoints as main NLT app for seamless sync
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get auth token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const messagesApi = {
  // Send a direct message
  send: async (data) => {
    const res = await axios.post(`${API_URL}/api/messages`, data, {
      headers: getAuthHeaders()
    });
    return res.data;
  },

  // Upload an image or video for a message -> returns { url, mediaType }
  uploadMedia: async (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API_URL}/api/messages/upload`, fd, {
      headers: getAuthHeaders(),
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    return res.data;
  },

  // Get messages with a specific user
  get: async (otherUserId) => {
    const res = await axios.get(`${API_URL}/api/messages/${otherUserId}`, {
      headers: getAuthHeaders()
    });
    return res.data;
  },

  // Get message thread with another user (marks as read)
  getThread: async (otherUserId) => {
    const res = await axios.get(`${API_URL}/api/messages/thread/${otherUserId}`, {
      headers: getAuthHeaders()
    });
    return res.data;
  },

  // Get unread message count
  getUnreadCount: async () => {
    const res = await axios.get(`${API_URL}/api/messages/unread-count`, {
      headers: getAuthHeaders()
    });
    // Backend returns unread_count, normalize to count for frontend
    return { count: res.data.unread_count || 0 };
  },

  // Get list of conversations
  getConversations: async (sort = 'newest') => {
    const res = await axios.get(`${API_URL}/api/messages/conversations?sort=${sort}`, {
      headers: getAuthHeaders()
    });
    // Normalize backend response to match frontend expectations
    const conversations = (res.data.conversations || []).map(conv => ({
      id: conv.userId,
      otherUser: {
        id: conv.userId,
        name: conv.name,
        username: conv.username,
        profilePhoto: conv.profilePhoto
      },
      lastMessage: {
        text: conv.lastMessage,
        createdAt: conv.lastMessageTime
      },
      unreadCount: conv.unreadCount,
      updatedAt: conv.lastMessageTime
    }));
    return { conversations };
  },

  // Mark messages from a user as read
  markAsRead: async (otherUserId) => {
    const res = await axios.post(`${API_URL}/api/messages/read/${otherUserId}`, {}, {
      headers: getAuthHeaders()
    });
    return res.data;
  }
};

export default messagesApi;
